import React, { useEffect, useState } from "react";

import { ProductData } from "data/product/products";
import { format } from "date-fns";
import { Icon, IconVariant } from "studio-design-system";

import { DotSeparatorIcon } from "~/components/common/Icons/DotSeparatorIcon";
import { InformationIcon } from "~/components/common/Icons/InformationIcon";
import { RatingStarIcon } from "~/components/common/Icons/RatingStarIcon";
import { ProfilePic } from "~/components/common/ProfilePic";
import { useAllReviewFunctions } from "~/hooks/review/useReview";
import { useAllReviewModalFunctions } from "~/hooks/review/useReviewModal";
import { convertSecondsToFriendlyDuration, magicConversion } from "~/util/stringUtils";

import * as styles from "./SeriesDetails.css";

export const SeriesDetails = ({
  productData,
  showDescription = false,
  setIsDetailsModalOpen,
  setIsPlaying,
}: {
  productData: ProductData;
  showDescription: boolean;
  setIsDetailsModalOpen: (isOpen: boolean) => void;
  setIsPlaying: (isPlaying: boolean) => void;
}): React.ReactElement => {
  const { currentReview, getReview } = useAllReviewFunctions();
  const { openReviewModal, setProductInfo, isReviewModalOpen } = useAllReviewModalFunctions();
  const [closeReviewModal, setCloseReviewModal] = useState(false);

  useEffect(() => {
    getReview(productData.id);
  }, [getReview, productData.id]);

  useEffect(() => {
    if (isReviewModalOpen) {
      setIsPlaying(false);
      setCloseReviewModal(true);
    } else if (closeReviewModal) {
      setCloseReviewModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewModalOpen]);

  const onReviewClick = React.useCallback(() => {
    openReviewModal();
    setProductInfo(productData);
  }, [openReviewModal, setProductInfo, productData]);

  return (
    <div className={styles.seriesWrap}>
      <h2 className={styles.seriesTitle}>{productData.title}</h2>
      <div className={styles.creatorBox}>
        <div className={styles.creatorImgWrap}>
          <ProfilePic
            size={24}
            imageUrl={productData.creatorProfileImageURL}
            firstName={productData.creatorName}
          />
        </div>
        {productData.creatorName}
        <Icon
          className={styles.verifiedIcon}
          variant={IconVariant.CircleCheck}
          width={14}
          height={14}
        />
        <DotSeparatorIcon />
        <span className={styles.whiteSpace}>
          {convertSecondsToFriendlyDuration(productData.totalVideoLengthInSeconds)}
        </span>
        <DotSeparatorIcon />
        <span className={styles.whiteSpace}>
          {format(new Date(productData.createdAt), "MMM dd, yyyy")}
        </span>
      </div>
      <div className={styles.buttonsContainer}>
        <button className={styles.button} onClick={onReviewClick}>
          <RatingStarIcon />
          {currentReview ? "Edit your rating" : "Leave a rating"}
        </button>
        <button className={styles.button} onClick={() => setIsDetailsModalOpen(true)}>
          <InformationIcon />
          Show details
        </button>
      </div>
      {showDescription && (
        <p
          className={styles.seriesDesc}
          dangerouslySetInnerHTML={{ __html: magicConversion(productData.description) }}
        ></p>
      )}
    </div>
  );
};
